// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useMemo } from 'react';

import russia from 'assets/map/russia.json';
import {
    Geometry,
    HiddenStates,
    StateData,
    StateGroupData,
    TopoJSON,
} from 'types/map';

const rus = russia as unknown as TopoJSON;

type UseMapDataReturn = {
    data: TopoJSON;
};

type UseMapDataOptions = {
    hidden?: HiddenStates;
    states?: StateData[];
    groups?: StateGroupData[];
};

const addPropertiesToAll = (
    data: TopoJSON,
    callbackFn: (geo: Geometry) => Geometry
): TopoJSON => {
    return {
        ...data,
        objects: {
            ...data.objects,
            russia: {
                ...data.objects.russia,
                geometries: data.objects.russia.geometries.map(callbackFn),
            },
        },
    };
};

const mergePolygonsByGroupID = (geometry: Geometry[]): Geometry[] => {
    return geometry.reduce<Geometry[]>((acc, { arcs, properties, type }) => {
        const item = acc.find(
            (a) => properties.group && a.properties.group === properties.group
        );

        if (item) {
            return acc.map((el) => {
                if (el.properties.id === item.properties.id) {
                    return {
                        type: 'MultiPolygon',
                        properties,
                        arcs:
                            type === 'MultiPolygon'
                                ? [...el.arcs, ...arcs]
                                : [...el.arcs, ...[arcs]],
                    };
                }
                return el;
            });
        }

        return [
            ...acc,
            {
                type: 'MultiPolygon',
                properties,
                arcs: type === 'MultiPolygon' ? arcs : [arcs],
            },
        ];
    }, []);
};

export const useMapData = (options?: UseMapDataOptions): UseMapDataReturn => {
    const { groups, hidden, states } = options || {};

    const mapData = useMemo(
        () =>
            addPropertiesToAll(rus, (geo) => {
                const group = groups?.find((g) =>
                    g.states.includes(geo.properties.id)
                );
                const state = states?.find((s) => s.id === geo.properties.id);

                const name = (): string => {
                    if (group?.name !== undefined) {
                        return group?.name;
                    }
                    if (state?.name !== undefined) {
                        return state.name;
                    }
                    return geo.properties.full_name;
                };

                return {
                    ...geo,
                    properties: {
                        ...geo.properties,
                        group: group?.id,
                        color: group?.color || state?.color || '#03B481',
                        name: name(),
                        hrefUuid:
                            group?.hrefUuid || state?.hrefUuid || undefined,
                        hidden: hidden?.includes(geo.properties.id),
                    },
                };
            }),
        [groups, hidden, states]
    );

    const groupedMapData = useMemo(
        () =>
            groups?.length
                ? mergePolygonsByGroupID(mapData.objects.russia.geometries)
                : mapData.objects.russia.geometries,
        [groups?.length, mapData]
    );

    const mergedMapData = useMemo(() => {
        return {
            ...mapData,
            objects: {
                ...mapData.objects,
                russia: {
                    ...mapData.objects.russia,
                    geometries: groupedMapData.filter(
                        (el) => !el.properties.hidden
                    ),
                },
            },
        };
    }, [groupedMapData, mapData]);

    return {
        data: mergedMapData,
    };
};
